import React, { FC, ReactNode, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";

import ChevronDownIcon from "../../icons/ChevronDownIcon";
import {
  sidebarAccordionSx,
  sidebarAccordionSummarySx,
  sidebarAccordionTitleSx,
  sidebarAccordionContentSx,
} from "./sidebarAccordionStyles";

interface SidebarAccordionProps {
  title: string;
  icon: ReactNode;
  defaultExpanded?: boolean;
  flexGrow?: number;
  children: ReactNode;
}

const SidebarAccordion: FC<SidebarAccordionProps> = ({
  title,
  icon,
  defaultExpanded = false,
  flexGrow = 1,
  children,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box
      sx={{
        ...sidebarAccordionSx,
        ...(expanded
          ? { flex: `${flexGrow} 1 0`, minHeight: 0, maxHeight: "max-content" }
          : {}),
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
        sx={[
          sidebarAccordionSummarySx,
          {
            cursor: "pointer",
            userSelect: "none",
            "&:hover": { bgcolor: "action.hover" },
          },
        ]}
      >
        {icon}
        <Typography variant="caption" sx={sidebarAccordionTitleSx(theme)}>
          {title}
        </Typography>

        <Box
          sx={{
            ml: "auto",
            display: "flex",
            alignItems: "center",
            color: theme.palette.custom.brandTeal,
            transform: expanded ? "rotate(180deg)" : "none",
            transition: "transform 0.15s ease",
          }}
        >
          <ChevronDownIcon className="w-2 h-1" />
        </Box>
      </Box>
      
      {expanded && (
        <Box sx={sidebarAccordionContentSx}>{children}</Box>
      )}
    </Box>
  );
};

export default SidebarAccordion;
